import React, { useEffect, useState } from "react";

const inputClass =
  "w-full rounded-lg border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200";

// ============================================================
// REUSABLE STEP CONTAINER
// ============================================================

export function KycStepShell({ title, children, maxWidth = "max-w-4xl" }) {
  return (
    <div className="mt-5 flex justify-center">
      <div
        className={`w-full ${maxWidth} rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8`}
      >
        <h3 className="mb-8 text-center text-xl font-bold text-gray-900 sm:text-2xl">
          {title}
        </h3>

        {children}
      </div>
    </div>
  );
}

// ============================================================
// REUSABLE TEXT INPUT
// ============================================================

export function KycTextField({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  required = true,
  uppercase = false,
  type = "text",
}) {
  return (
    <label className="block min-w-0">
      <span className="mb-2 block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </span>

      <input
        type={type}
        value={value ?? ""}
        maxLength={maxLength}
        placeholder={placeholder}
        required={required}
        onChange={(event) =>
          onChange(
            uppercase ? event.target.value.toUpperCase() : event.target.value,
          )
        }
        className={`${inputClass} ${uppercase ? "uppercase" : ""}`}
      />
    </label>
  );
}

// ============================================================
// REUSABLE IMAGE UPLOAD
// Used for Profile, Aadhaar, PAN, DL and Vehicle
// ============================================================

export function KycImageUpload({
  label,
  value,
  onChange,
  field,
  rounded = false,
}) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (typeof value === "string") {
      setPreviewUrl(value);
      return;
    }

    if (typeof Blob !== "undefined" && value instanceof Blob) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);

      return () => URL.revokeObjectURL(url);
    }

    setPreviewUrl("");
  }, [value]);

  return (
    <div className="flex min-h-[180px] min-w-0 flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 p-5 text-center">
      <span className="mb-3 text-sm font-medium text-gray-700">
        {label} <span className="text-red-500">*</span>
      </span>

      {previewUrl ? (
        <div className="w-full space-y-3">
          <img
            src={previewUrl}
            alt={label}
            className={`mx-auto h-36 w-full max-w-xs border object-contain shadow-sm ${
              rounded ? "rounded-full" : "rounded-lg"
            }`}
          />

          <p className="break-words rounded-lg bg-green-50 px-3 py-2 text-xs text-green-700">
            {typeof value === "string"
              ? `Current ${label}`
              : value?.name || "Selected image"}
          </p>

          <div className="flex flex-wrap justify-center gap-2">
            <label className="cursor-pointer rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600">
              Change
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];

                  if (file) onChange(field, file);

                  e.target.value = "";
                }}
              />
            </label>

            <button
              type="button"
              onClick={() => onChange(field, null)}
              className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-400">No file selected</p>

          <label className="inline-block cursor-pointer rounded-lg border border-orange-600 bg-white px-4 py-2 text-sm text-orange-600 hover:bg-orange-50">
            Choose File
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];

                if (file) onChange(field, file);

                e.target.value = "";
              }}
            />
          </label>
        </div>
      )}
    </div>
  );
}

// ============================================================
// REUSABLE NEXT / BACK / SUBMIT BUTTONS
// ============================================================

export function KycNavigation({
  onBack,
  onNext,
  loading = false,
  last = false,
}) {
  return (
    <div className="mt-10 flex flex-wrap justify-end gap-3">
      {onBack && (
        <button
          type="button"
          disabled={loading}
          onClick={onBack}
          className="rounded-lg bg-gray-500 px-8 py-3 font-medium text-white shadow-sm hover:bg-gray-600 disabled:opacity-60"
        >
          Back
        </button>
      )}

      <button
        type="button"
        disabled={loading}
        onClick={onNext}
        className="rounded-lg bg-orange-500 px-8 py-3 font-medium text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading
          ? last
            ? "Submitting..."
            : "Saving..."
          : last
            ? "Submit"
            : "Next"}
      </button>
    </div>
  );
}
